// Moments manager. Singleton that owns the feed state and mediates
// between the IndexedDB store, the capture helper, and the WebSocket.
//
// Surface:
//   - subscribe(fn)        → pub/sub on merged feed changes
//   - captureAndPost()     → take a screenshot + broadcast to friends
//   - receive(msg.data)    → called by presenceManager when a friend
//                            posts and we receive a moment:received
//                            WebSocket event
//   - getFeed()            → current merged feed, newest first
//   - init()               → hydrate from IndexedDB on app start
//   - clear()              → wipe on logout
//
// Feed shape per entry:
//   { id, kind: 'self' | 'friend', authorId, authorName, authorAvatar,
//     createdAt, blob, width, height, url }
// `url` is a lazily-created object URL pointing at `blob`. Callers
// should render it in an <img src=url>; the manager revokes old URLs
// on eviction / clear so we don't leak.

import { listBucket, insertIntoBucket, clearAll, MOMENTS_CAP } from './momentsStore.js';
import { captureMoment, blobToBase64, base64ToBlob } from './captureMoment.js';
import { send as wsSend } from '../serverClient';
import { sendChatMessage } from '../arena/chatApi';

const SELF = 'self';
const FRIEND = 'friend';

let self = [];
let friends = [];
let listeners = new Set();
let objectUrls = new Map(); // id -> object URL

function emit() {
  const merged = mergeFeed(self, friends);
  for (const fn of listeners) {
    try { fn(merged); } catch (err) { console.error('[moments] listener error:', err); }
  }
}

function mergeFeed(selfList, friendList) {
  // Both lists arrive sorted newest-first already (the store reads in
  // descending createdAt order), so a standard merge keeps O(n+m) and
  // avoids resorting the whole thing each emit.
  const out = [];
  let i = 0, j = 0;
  while (i < selfList.length && j < friendList.length) {
    if (selfList[i].createdAt >= friendList[j].createdAt) out.push(selfList[i++]);
    else out.push(friendList[j++]);
  }
  while (i < selfList.length) out.push(selfList[i++]);
  while (j < friendList.length) out.push(friendList[j++]);
  return out;
}

function urlFor(entry) {
  const existing = objectUrls.get(entry.id);
  if (existing) return existing;
  const url = URL.createObjectURL(entry.blob);
  objectUrls.set(entry.id, url);
  return url;
}

function decorate(entry) {
  return { ...entry, url: urlFor(entry) };
}

function revokeEvicted(next, prev) {
  // Any id present in prev but missing from next has been evicted;
  // revoke its object URL so memory is released.
  const nextIds = new Set(next.map((e) => e.id));
  for (const entry of prev) {
    if (!nextIds.has(entry.id)) {
      const url = objectUrls.get(entry.id);
      if (url) {
        URL.revokeObjectURL(url);
        objectUrls.delete(entry.id);
      }
    }
  }
}

export async function init() {
  try {
    const [s, f] = await Promise.all([listBucket(SELF), listBucket(FRIEND)]);
    self = s.map(decorate);
    friends = f.map(decorate);
    emit();
  } catch (err) {
    console.error('[moments] init failed:', err);
  }
}

export function getFeed() {
  return mergeFeed(self, friends);
}

export function subscribe(fn) {
  listeners.add(fn);
  // Emit current state so late subscribers paint immediately.
  try { fn(mergeFeed(self, friends)); } catch (err) { console.error(err); }
  return () => { listeners.delete(fn); };
}

// Take a screenshot, persist locally, broadcast to friends.
//
// The manager posts optimistically. The entry lands in the local
// feed before the server responds. The server may later send
// moment:ack (authoritative createdAt) or moment:error (rate limit /
// oversized); the ack path refreshes createdAt, the error path wipes
// the optimistic entry and the caller's promise rejects.
export async function captureAndPost({ selfProfile }) {
  const captured = await captureMoment();
  const id = `m-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  const entry = {
    id,
    kind: SELF,
    authorId: selfProfile?.id || 'self',
    authorName: selfProfile?.name || selfProfile?.displayName || 'You',
    authorAvatar: selfProfile?.profileAvatar || null,
    createdAt: Date.now(),
    blob: captured.blob,
    width: captured.width,
    height: captured.height,
  };
  const prev = self;
  const stored = await insertIntoBucket(SELF, entry);
  // Fresh list read so we honour the cap (the store prunes on insert).
  const rawSelf = await listBucket(SELF);
  self = rawSelf.map(decorate);
  revokeEvicted(self, prev);
  emit();

  // Broadcast. We await the base64 conversion so we don't ship a
  // partially-read blob if the caller does something else first.
  const webp = await blobToBase64(captured.blob);
  wsSend('moment:post', {
    id,
    webp,
    width: captured.width,
    height: captured.height,
  });
  return stored;
}

// Handle moment:received from the server. Runs in the presence manager
// dispatch loop.
export async function receive(data) {
  if (!data?.id || !data?.webp) return;
  const blob = base64ToBlob(data.webp, 'image/webp');
  const entry = {
    id: data.id,
    kind: FRIEND,
    authorId: data.authorId,
    authorName: data.authorName || 'Unknown',
    authorAvatar: data.authorAvatar || null,
    createdAt: data.createdAt || Date.now(),
    blob,
    width: data.width || null,
    height: data.height || null,
  };
  const prev = friends;
  await insertIntoBucket(FRIEND, entry);
  const raw = await listBucket(FRIEND);
  friends = raw.map(decorate);
  revokeEvicted(friends, prev);
  emit();
}

// Send an existing feed entry (or freshly captured blob) to a friend
// as a chat attachment. The server stores it under chat_messages with
// type='moment' so it lands in their inbox + scrolls into the chat
// view like any other message. Returns the inserted message shape
// from the server, which the caller can optimistically render.
export async function sendMomentToFriend(friendId, entry) {
  if (!friendId || !entry?.blob) throw new Error('friendId + moment blob required');
  const webp = await blobToBase64(entry.blob);
  const payload = {
    type: 'moment',
    metadata: {
      webp,
      width: entry.width || null,
      height: entry.height || null,
    },
  };
  console.log('[moments] sendMomentToFriend',
    'friendId=', friendId,
    'type=', payload.type,
    'webpLength=', webp?.length,
    'webpFirst32=', webp?.slice(0, 32),
    'width=', payload.metadata.width,
    'height=', payload.metadata.height,
  );
  try {
    const result = await sendChatMessage(friendId, payload);
    console.log('[moments] sendMomentToFriend OK', result);
    return result;
  } catch (err) {
    console.error('[moments] sendMomentToFriend FAIL. Server rejected:', err?.message);
    throw err;
  }
}

// Capture-only helper. Returns { blob, width, height } without
// saving to the feed or broadcasting. Callers use this to feed
// sendMomentToFriend for one-shot non-feed flows (e.g. chat
// "capture new and send" action).
export async function captureMomentBlob() {
  return captureMoment();
}

export async function clear() {
  for (const url of objectUrls.values()) URL.revokeObjectURL(url);
  objectUrls.clear();
  self = [];
  friends = [];
  emit();
  try { await clearAll(); } catch (err) { console.error('[moments] clear failed:', err); }
}

export { MOMENTS_CAP };
